import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Spin, message } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { setCredentials } from '../../store/slices/authSlice';
import './ResetShared.css';

const { Title, Text } = Typography;

interface VerificationFormData {
    new_password: string;
    confirm_new_password: string;
}

const VerifyAccount: React.FC = () => {
    const [form] = Form.useForm();
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [loading, setLoading] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(true);
    const [userInfo, setUserInfo] = useState<{ username: string; full_name: string } | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (token) {
            verifyToken();
        } else {
            setError('Invalid verification link.');
            setVerifyLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await fetch(`/v1/auth/verify/?token=${token}`);
            if (response.ok) {
                const data = await response.json();
                setUserInfo(data);
                setError(null);
            } else {
                const data = await response.json();
                setError(data.detail || 'Invalid or expired verification link.');
            }
        } catch (_err) {
            setError('Failed to verify token.');
        } finally {
            setVerifyLoading(false);
        }
    };

    const onFinish = async (values: VerificationFormData) => {
        if (!token) {
            message.error('Invalid verification link.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('/v1/auth/verify/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token,
                    new_password: values.new_password,
                    confirm_new_password: values.confirm_new_password,
                }),
            });

            const data = await response.json();
            if (response.ok) {
                localStorage.setItem('access_token', data.access);
                localStorage.setItem('refresh_token', data.refresh);
                dispatch(
                    setCredentials({
                        user: data.user,
                        token: data.access,
                        refreshToken: data.refresh,
                    })
                );
                message.success('Account verified! Redirecting to dashboard...');
                setTimeout(() => navigate('/dashboard'), 1000);
            } else {
                const detail = Array.isArray(data.detail) ? data.detail.join(' ') : data.detail;
                message.error(detail || 'Verification failed.');
            }
        } catch (_err) {
            message.error('Verification failed.');
        } finally {
            setLoading(false);
        }
    };

    if (verifyLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error || !userInfo) {
        return (
            <div className="reset-container">
                <div className="reset-content">
                    <Card className="reset-card">
                        <Title level={2}>Account Verification</Title>
                        <Alert message={error || 'Invalid verification link.'} type="error" showIcon />
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <Button type="primary" onClick={() => navigate('/login')}>
                                Back to Login
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-container">
            <div className="reset-content">
                <Card className="reset-card">
                    <div className="reset-header">
                        <Title level={2}>Set Your Password</Title>
                        <Text type="secondary">
                            Welcome, {userInfo.full_name || userInfo.username}. Choose a strong password to activate your account.
                        </Text>
                    </div>

                    <Form
                        form={form}
                        name="verify"
                        onFinish={onFinish}
                        layout="vertical"
                        size="large"
                        className="reset-form"
                    >
                        <Form.Item
                            name="new_password"
                            label="New Password"
                            rules={[
                                { required: true, message: 'Please input your new password!' },
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.reject(new Error('Please input your new password!'));
                                        const hasLength = value.length >= 8;
                                        const hasNumber = /\d/.test(value);
                                        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(value);
                                        const hasLower = /[a-z]/.test(value);
                                        if (hasLength && hasNumber && hasSpecial && hasLower) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            new Error('Password must be 8+ characters and include a number, special character, and lowercase letter.')
                                        );
                                    },
                                },
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder="Enter new password"
                                autoComplete="new-password"
                            />
                        </Form.Item>

                        <Form.Item
                            name="confirm_new_password"
                            label="Confirm New Password"
                            dependencies={['new_password']}
                            rules={[
                                { required: true, message: 'Please confirm your new password!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('new_password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('The two passwords do not match!'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder="Confirm new password"
                                autoComplete="new-password"
                            />
                        </Form.Item>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                className="reset-button"
                                loading={loading}
                                block
                            >
                                {loading ? <Spin size="small" /> : 'Activate Account'}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </div>
    );
};

export default VerifyAccount;
